'use client';

import React, { useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import {
  PhonicsLesson,
  PhonicsActivity,
  PhonicsActivityTypeMode,
} from '@/api/types';
import { useLessonPlayer } from '@/context/LessonPlayer';
import {
  Carousel,
  CarouselSlide,
  CarouselSlideProps,
  RoundCloseButton,
  RoundPlayButton,
  RoundRefreshButton,
} from '@/components';
import { ProgressDots, PillButton } from '@/components/lessonKit';
import { makeSoundsStringForLesson } from '@/utils/sounds';
import { getActivityStage, isClayActivity } from './activityStage';
import {
  ReadingReviewSounds,
  ReadingNewSounds,
  ReadingListening,
  ReadingOralBlending,
  ReadingManipulatingSounds,
  ReadingReadWords,
  ReadingTrickyWords,
  ReadingReadSentences,
  ReadingFocusSounds,
  ReadingSingularOrPlural,
  ReadingAddSuffixes,
  ReadingNounOrVerb,
  ReadingFormThirdPersonSingular,
  ReadingNewSuffix,
  ReadingManipulatingPrefixesAndSuffixes,
  ReadingReviewRootWords,
  ReadingReviewRootWordsAndSuffixes,
  SpellingReviewSounds,
  SpellingNewSounds,
  SpellingOralSegmenting,
  SpellingWriteWords,
  SpellingWriteSentences,
  SpellingFocusSounds,
  SpellingNounOrVerb,
  SpellingReviewRootWordsAndSuffixes,
} from './activityTypes';
import {
  PhonicsLessonPlayerKind,
  ACTIVITY_TYPE_READING_REVIEW_SOUNDS_IDENTIFIER,
  ACTIVITY_TYPE_READING_NEW_SOUNDS_IDENTIFIER,
  ACTIVITY_TYPE_READING_LISTENING_IDENTIFIER,
  ACTIVITY_TYPE_READING_ORAL_BLENDING_IDENTIFIER,
  ACTIVITY_TYPE_READING_MANIPULATING_SOUNDS_IDENTIFIER,
  ACTIVITY_TYPE_READING_READ_WORDS_IDENTIFIER,
  ACTIVITY_TYPE_READING_TRICKY_WORDS_IDENTIFIER,
  ACTIVITY_TYPE_READING_READ_SENTENCES_IDENTIFIER,
  ACTIVITY_TYPE_READING_FOCUS_SOUNDS_IDENTIFIER,
  ACTIVITY_TYPE_READING_SINGULAR_OR_PLURAL_IDENTIFIER,
  ACTIVITY_TYPE_READING_ADD_SUFFIXES_IDENTIFIER,
  ACTIVITY_TYPE_READING_NOUN_OR_VERB_IDENTIFIER,
  ACTIVITY_TYPE_READING_NEW_SUFFIX_IDENTIFIER,
  ACTIVITY_TYPE_READING_REVIEW_ROOT_WORDS_IDENTIFIER,
  ACTIVITY_TYPE_READING_REVIEW_ROOT_WORDS_AND_SUFFIXES_IDENTIFIER,
  ACTIVITY_TYPE_READING_FORM_THIRD_PERSON_SINGULAR_IDENTIFIER,
  ACTIVITY_TYPE_READING_MANIPULATING_PREFIXES_AND_SUFFIXES_IDENTIFIER,
  ACTIVITY_TYPE_SPELLING_REVIEW_SOUNDS_IDENTIFIER,
  ACTIVITY_TYPE_SPELLING_NEW_SOUNDS_IDENTIFIER,
  ACTIVITY_TYPE_SPELLING_FLICKING_FINGERS_IDENTIFIER,
  ACTIVITY_TYPE_SPELLING_WRITE_WORDS_IDENTIFIER,
  ACTIVITY_TYPE_SPELLING_WRITE_SENTENCES_IDENTIFIER,
  ACTIVITY_TYPE_SPELLING_FOCUS_SOUNDS_IDENTIFIER,
  ACTIVITY_TYPE_SPELLING_NOUN_OR_VERB_IDENTIFIER,
  ACTIVITY_TYPE_SPELLING_REVIEW_ROOT_WORDS_AND_SUFFIXES_IDENTIFIER,
  // ACTIVITY_TYPE_SOUND_DICTATION_IDENTIFIER,
} from './types';
import { LearningLink } from './components/LearningLink';
import { LearningLauncher } from './components/LearningLauncher';
import { MsPaint } from './components/MsPaint';
import { LessonFinishedStep } from './components/LessonFinishedStep';
import { LockScreen } from './components/LockScreen';
import { LeaveLessonDialog } from './components/LeaveLessonDialog';
import { ClassStatus } from './components/ClassStatus';
export { WhitePanel } from './components/WhitePanel';

// Carousel nav adapters — DS pill buttons wired through the Carousel's own
// prev/next (so canScroll gating + onBeforeChange still apply). The Carousel
// injects onClick/className/disabled.
interface NavPillProps {
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  color?: string;
  style?: React.CSSProperties;
}

const NavBackPill = ({
  onClick,
  className,
  disabled,
  color,
  style,
}: NavPillProps) => (
  <PillButton
    variant={color || style ? 'primary' : 'ghost'}
    size="sm"
    arrow="left"
    color={color}
    onClick={onClick}
    disabled={disabled}
    className={className}
    style={style}
  >
    BACK
  </PillButton>
);

const NavNextPill = ({
  onClick,
  className,
  disabled,
  color,
  style,
}: NavPillProps) => (
  <PillButton
    size="sm"
    arrow="right"
    color={color}
    onClick={onClick}
    disabled={disabled}
    className={className}
    style={style}
  >
    NEXT
  </PillButton>
);

interface LessonPlayerProps {
  lesson: PhonicsLesson;
  lessonLoading: boolean;
  isLive?: boolean;
  error?: string;
  mode?: PhonicsActivityTypeMode;
  kind?: PhonicsLessonPlayerKind;
  isPreview?: boolean;
  initialActivityId?: string;
  filterActivityIds?: Array<string>;
  onBeforeChange?: (slideIndex: number, activityId?: string) => boolean;
  onSlideSettle?: (slideIndex: number, activityStepId?: string) => void;
  onChange?: (
    slideIndex: number,
    lastSlideIndex?: number,
    activityStepId?: string,
    lastActivityStepId?: string,
  ) => void;
  onStart?: () => void;
  onComplete?: () => void;
  onClose?: () => void;
}

export const LessonPlayer = ({
  lesson,
  lessonLoading = false,
  isLive = false,
  error,
  mode = PhonicsActivityTypeMode.TEACH,
  kind = 'reading',
  isPreview = false,
  initialActivityId,
  filterActivityIds,
  onBeforeChange,
  onSlideSettle,
  onChange,
  onStart,
  onComplete,
  onClose,
}: LessonPlayerProps) => {
  const {
    carouselRef,
    getActivityProgress,
    playButtonProps,
    refreshButtonProps,
  } = useLessonPlayer();

  const [slides, setSlides] = useState<
    Array<React.ReactElement<CarouselSlideProps>>
  >([]);

  const [showLockScreen, setShowLockScreen] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  // Ordered activity ids (for the smartboard-chrome progress dots) + which one
  // is currently on screen. Display-only; derived from the same slide-change
  // signal the metrics use, so it never affects scoring/registration.
  const [activityIds, setActivityIds] = useState<Array<string>>([]);

  const [currentActivityIndex, setCurrentActivityIndex] = useState<number>(0);

  // Which slide is on screen — drives the smartboard progress dots so they
  // advance on every real navigation step, not just per activity.
  const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0);

  const lessonSounds = lesson.sounds
    ? makeSoundsStringForLesson(lesson, '/')
    : '';

  const currentActivity = lesson.activities.find(
    (activity) => activity.id === activityIds[currentActivityIndex],
  );

  const currentStage = currentActivity
    ? getActivityStage(
        currentActivity.type.identifier,
        currentActivity.type.mode,
      )
    : 'action';

  // Clay (pupil-app) activities use a warm gold field, so the shared chrome
  // switches to readable dark-green ink + green clay controls over it.
  const clay =
    !!currentActivity && isClayActivity(currentActivity.type.identifier);
  const nextColor = clay ? '#0E5A2A' : `var(--c-${currentStage})`;

  // Clay paging buttons match the design: solid green pills with a hard lip.
  // Literal hex (not --clay-* vars) because the player chrome sits outside the
  // .theme-app scope where those tokens are defined.
  const clayPillStyle: React.CSSProperties | undefined = clay
    ? { background: '#2A8049', color: '#fff', boxShadow: '0 6px 0 #1C6B38' }
    : undefined;

  const getActivityLayout = useCallback(
    (activity: PhonicsActivity) => {
      switch (activity.type.identifier) {
        case ACTIVITY_TYPE_READING_REVIEW_SOUNDS_IDENTIFIER:
          return (
            <ReadingReviewSounds
              key={activity.id}
              activity={activity}
              mode={mode}
              isPreview={isPreview}
            />
          );

        case ACTIVITY_TYPE_READING_NEW_SOUNDS_IDENTIFIER:
          return (
            <ReadingNewSounds
              key={activity.id}
              activity={activity}
              mode={mode}
              isPreview={isPreview}
            />
          );

        case ACTIVITY_TYPE_READING_LISTENING_IDENTIFIER:
          return (
            <ReadingListening
              key={activity.id}
              activity={activity}
              mode={mode}
              isPreview={isPreview}
            />
          );

        case ACTIVITY_TYPE_READING_ORAL_BLENDING_IDENTIFIER:
          return (
            <ReadingOralBlending
              key={activity.id}
              activity={activity}
              mode={mode}
              isPreview={isPreview}
            />
          );

        case ACTIVITY_TYPE_READING_MANIPULATING_SOUNDS_IDENTIFIER:
          return (
            <ReadingManipulatingSounds
              key={activity.id}
              activity={activity}
              mode={mode}
              isPreview={isPreview}
            />
          );

        case ACTIVITY_TYPE_READING_READ_WORDS_IDENTIFIER:
          return (
            <ReadingReadWords
              key={activity.id}
              activity={activity}
              mode={mode}
              isPreview={isPreview}
            />
          );

        case ACTIVITY_TYPE_READING_TRICKY_WORDS_IDENTIFIER:
          return (
            <ReadingTrickyWords
              key={activity.id}
              activity={activity}
              mode={mode}
              isPreview={isPreview}
            />
          );

        case ACTIVITY_TYPE_READING_READ_SENTENCES_IDENTIFIER:
          return (
            <ReadingReadSentences
              key={activity.id}
              activity={activity}
              mode={mode}
              isPreview={isPreview}
            />
          );

        case ACTIVITY_TYPE_READING_FOCUS_SOUNDS_IDENTIFIER:
          return (
            <ReadingFocusSounds
              key={activity.id}
              activity={activity}
              mode={mode}
              isPreview={isPreview}
            />
          );

        case ACTIVITY_TYPE_READING_SINGULAR_OR_PLURAL_IDENTIFIER:
          return (
            <ReadingSingularOrPlural
              key={activity.id}
              activity={activity}
              mode={mode}
              isPreview={isPreview}
            />
          );

        case ACTIVITY_TYPE_READING_ADD_SUFFIXES_IDENTIFIER:
          return (
            <ReadingAddSuffixes
              key={activity.id}
              activity={activity}
              mode={mode}
              isPreview={isPreview}
            />
          );

        case ACTIVITY_TYPE_READING_NOUN_OR_VERB_IDENTIFIER:
          return (
            <ReadingNounOrVerb
              key={activity.id}
              activity={activity}
              mode={mode}
              isPreview={isPreview}
            />
          );

        case ACTIVITY_TYPE_READING_FORM_THIRD_PERSON_SINGULAR_IDENTIFIER:
          return (
            <ReadingFormThirdPersonSingular
              key={activity.id}
              activity={activity}
              mode={mode}
              isPreview={isPreview}
            />
          );

        case ACTIVITY_TYPE_READING_NEW_SUFFIX_IDENTIFIER:
          return (
            <ReadingNewSuffix
              key={activity.id}
              activity={activity}
              mode={mode}
              isPreview={isPreview}
            />
          );

        case ACTIVITY_TYPE_READING_MANIPULATING_PREFIXES_AND_SUFFIXES_IDENTIFIER:
          return (
            <ReadingManipulatingPrefixesAndSuffixes
              key={activity.id}
              activity={activity}
              mode={mode}
              isPreview={isPreview}
            />
          );

        case ACTIVITY_TYPE_READING_REVIEW_ROOT_WORDS_IDENTIFIER:
          return (
            <ReadingReviewRootWords
              key={activity.id}
              activity={activity}
              mode={mode}
              isPreview={isPreview}
            />
          );

        case ACTIVITY_TYPE_READING_REVIEW_ROOT_WORDS_AND_SUFFIXES_IDENTIFIER:
          return (
            <ReadingReviewRootWordsAndSuffixes
              key={activity.id}
              activity={activity}
              mode={mode}
              isPreview={isPreview}
            />
          );

        case ACTIVITY_TYPE_SPELLING_REVIEW_SOUNDS_IDENTIFIER:
          return (
            <SpellingReviewSounds
              key={activity.id}
              activity={activity}
              mode={mode}
              isPreview={isPreview}
            />
          );

        case ACTIVITY_TYPE_SPELLING_NEW_SOUNDS_IDENTIFIER:
          return (
            <SpellingNewSounds
              key={activity.id}
              activity={activity}
              mode={mode}
              isPreview={isPreview}
            />
          );

        case ACTIVITY_TYPE_SPELLING_FLICKING_FINGERS_IDENTIFIER:
          return (
            <SpellingOralSegmenting
              key={activity.id}
              activity={activity}
              mode={mode}
              isPreview={isPreview}
            />
          );

        case ACTIVITY_TYPE_SPELLING_WRITE_WORDS_IDENTIFIER:
          return (
            <SpellingWriteWords
              key={activity.id}
              activity={activity}
              mode={mode}
              isPreview={isPreview}
            />
          );

        case ACTIVITY_TYPE_SPELLING_WRITE_SENTENCES_IDENTIFIER:
          return (
            <SpellingWriteSentences
              key={activity.id}
              activity={activity}
              mode={mode}
              isPreview={isPreview}
            />
          );

        case ACTIVITY_TYPE_SPELLING_FOCUS_SOUNDS_IDENTIFIER:
          return (
            <SpellingFocusSounds
              key={activity.id}
              activity={activity}
              mode={mode}
              isPreview={isPreview}
            />
          );

        case ACTIVITY_TYPE_SPELLING_NOUN_OR_VERB_IDENTIFIER:
          return (
            <SpellingNounOrVerb
              key={activity.id}
              activity={activity}
              mode={mode}
              isPreview={isPreview}
            />
          );

        case ACTIVITY_TYPE_SPELLING_REVIEW_ROOT_WORDS_AND_SUFFIXES_IDENTIFIER:
          return (
            <SpellingReviewRootWordsAndSuffixes
              key={activity.id}
              activity={activity}
              mode={mode}
              isPreview={isPreview}
            />
          );

        default:
          return (
            <CarouselSlide key={activity.id}>
              <p>{activity.type.name}</p>
              <p>{activity.description}</p>
            </CarouselSlide>
          );
      }
    },
    [isPreview, mode],
  );

  const tryClose = () => {
    if (isLive && mode === PhonicsActivityTypeMode.LEARN) {
      setShowLockScreen(true);

      return;
    }

    // Confirm before leaving, per the lesson-intro redesign.
    setShowLeaveConfirm(true);
  };

  useEffect(() => {
    console.log(
      `Playing lesson: ${lesson.number} ${kind} from step ${lesson.step} in ${isPreview ? 'PREVIEW' : mode} mode.`,
    ); // eslint-disable-line

    const includeLearningLink =
      mode === PhonicsActivityTypeMode.TEACH &&
      kind === 'reading' &&
      !isPreview;
    const includeLearningLauncher =
      mode === PhonicsActivityTypeMode.TEACH &&
      kind === 'spelling' &&
      !isPreview;
    const includeMsPaint = mode === PhonicsActivityTypeMode.LEARN;
    const includeLessonFinished = kind === 'spelling';

    const prefixSlides = [
      ...(includeLearningLauncher
        ? [<LearningLauncher key="learning-launcher" lesson={lesson} />]
        : []),
      ...(includeMsPaint
        ? [<MsPaint key="ms-paint-1" canClose={false} />]
        : []),
    ];

    const sufffixSlides = [
      ...(includeMsPaint ? [<MsPaint key="ms-paint-2" />] : []),
      ...(includeLearningLink
        ? [<LearningLink key="learning-link" lesson={lesson} />]
        : []),
      ...(includeLessonFinished
        ? [<LessonFinishedStep key="finished" lesson={lesson} />]
        : []),
    ];

    const activitySlides = lesson?.activities
      .filter(
        (activity) =>
          isPreview ||
          (mode === PhonicsActivityTypeMode.LEARN &&
            activity.type.mode === PhonicsActivityTypeMode.LEARN) ||
          (mode === PhonicsActivityTypeMode.TEACH &&
            kind === 'reading' &&
            activity.type.mode === PhonicsActivityTypeMode.TEACH) ||
          (mode === PhonicsActivityTypeMode.TEACH &&
            kind === 'spelling' &&
            activity.type.mode === PhonicsActivityTypeMode.LEARN),
      )
      .filter((activity) =>
        filterActivityIds ? filterActivityIds?.includes(activity.id) : true,
      );

    setActivityIds(activitySlides.map((activity) => activity.id));

    setSlides([
      ...prefixSlides,
      ...activitySlides.flatMap((activity) => getActivityLayout(activity)),
      ...sufffixSlides,
    ]);
  }, [lesson, mode, kind, isPreview, filterActivityIds, getActivityLayout]);

  useEffect(() => {
    if (!carouselRef?.current) {
      return;
    }

    carouselRef?.current.scrollTo(0);
  }, [carouselRef, kind]);

  return error ? (
    <p>Error fetching lesson: {error}</p>
  ) : lessonLoading ? (
    <p>Loading...</p>
  ) : slides ? (
    <div className="relative">
      <Carousel
        ref={carouselRef}
        className="w-full h-screen"
        toolsClassName="absolute bottom-0 left-0 right-0 z-20 flex items-center justify-between gap-3 px-8 pb-6 md:px-14"
        ariaLabel="Lesson activities"
        slides={slides}
        showProgressBar={false}
        initialSlideIdentifier={initialActivityId}
        navPrevElement={NavBackPill}
        navPrevElementProps={{
          className: 'pointer-events-auto',
          ariaLabel: 'Previous activity',
          color: clay ? '#0E5A2A' : undefined,
          style: clayPillStyle,
        }}
        navNextElement={NavNextPill}
        navNextElementProps={{
          className: 'pointer-events-auto',
          ariaLabel: 'Next activity',
          color: nextColor,
          style: clayPillStyle,
        }}
        extraTools={
          <div className="pointer-events-auto flex items-center gap-3">
            {!isPreview && mode === PhonicsActivityTypeMode.TEACH && (
              <ClassStatus />
            )}
            <RoundCloseButton
              onClick={() => tryClose()}
              size={mode === PhonicsActivityTypeMode.TEACH ? 'sm' : 'xs'}
            />
            <RoundPlayButton
              className={clsx(
                playButtonProps?.visible ? 'inline-block' : 'hidden',
              )}
              color="white"
              size={mode === PhonicsActivityTypeMode.TEACH ? 'sm' : 'xs'}
              onClick={playButtonProps?.onClick}
              disabled={playButtonProps?.disabled}
              pressHint={playButtonProps?.hint}
            />
            <RoundRefreshButton
              className={clsx(
                refreshButtonProps?.visible ? 'inline-block' : 'hidden',
              )}
              color="white"
              size={mode === PhonicsActivityTypeMode.TEACH ? 'sm' : 'xs'}
              onClick={refreshButtonProps?.onClick}
              disabled={refreshButtonProps?.disabled}
              pressHint={refreshButtonProps?.hint}
            />
          </div>
        }
        draggable={false}
        enableArrowKeys={true}
        onBeforeChange={onBeforeChange}
        onSettle={(slideIndex, slideIdentifier) => {
          onSlideSettle?.(slideIndex, slideIdentifier);
        }}
        onChange={async (
          slideIndex,
          lastSlideIndex,
          slideIdentifier,
          lastSlideIdentifier,
        ) => {
          document.dispatchEvent(
            new CustomEvent('hideTooltip', { bubbles: true }),
          );

          setCurrentSlideIndex(slideIndex);

          if (slideIndex === 0) {
            await onStart?.();
          }

          await onChange?.(
            slideIndex,
            lastSlideIndex,
            slideIdentifier,
            lastSlideIdentifier,
          );

          if (slideIdentifier) {
            getActivityProgress(slideIdentifier);

            const matchedActivityIndex = activityIds.findIndex((id) =>
              slideIdentifier.startsWith(id),
            );

            if (matchedActivityIndex >= 0) {
              setCurrentActivityIndex(matchedActivityIndex);
            }
          }

          if (
            mode === PhonicsActivityTypeMode.LEARN &&
            lesson?.activities &&
            slideIndex === lesson.activities.length - 1
          ) {
            await onComplete?.();
          }

          if (
            mode === PhonicsActivityTypeMode.TEACH &&
            slideIdentifier === 'learning-launcher'
          ) {
            await onComplete?.();
          }
        }}
      />

      {slides.length > 0 && (
        <div className="pointer-events-none absolute top-0 left-0 right-0 flex items-center justify-between px-8 py-6 md:px-14 md:py-7">
          <div
            className={clsx(
              'flex items-center gap-3 uppercase tracking-[3px] text-sm md:text-xl',
              clay ? 'text-pt-bottle-green' : 'text-white/85',
            )}
            style={{
              fontFamily: clay
                ? 'var(--clay-font), sans-serif'
                : 'var(--lp-display), Impact, sans-serif',
              fontWeight: clay ? 800 : undefined,
            }}
          >
            <span>Pickatale Phonics</span>
            <span className="opacity-50">·</span>
            <span>Lesson {lesson.number}</span>
            {lessonSounds && (
              <>
                <span className="opacity-50">·</span>
                <span>{lessonSounds}</span>
              </>
            )}
          </div>

          <ProgressDots
            total={slides.length}
            current={currentSlideIndex}
            tone={clay ? 'dark' : 'light'}
          />
        </div>
      )}

      {showLockScreen && (
        <LockScreen
          onClose={() => setShowLockScreen(false)}
          onUnlock={() => {
            setShowLockScreen(false);
            onClose?.();
          }}
        />
      )}

      {showLeaveConfirm && (
        <LeaveLessonDialog
          onStay={() => setShowLeaveConfirm(false)}
          onLeave={() => {
            setShowLeaveConfirm(false);
            onClose?.();
          }}
        />
      )}
    </div>
  ) : (
    'No activities found'
  );
};
